import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EnergyReportPanel from "./EnergyReportPanel";
import { foyerApi } from "../http/foyer";
import { errorText } from "../utils/apiError";
import type { EnergyRange, EnergyReport } from "@shared/types/foyer";

const RANGES: EnergyRange[] = ["day", "week", "month"];

/**
 * Chain-level energy + CO₂ rollup for one company, across all its premises.
 * Launched from the Companies list - the group ESG view a hotel chain's
 * sustainability lead wants, distinct from the per-property view in the premise
 * editor. Reads the same endpoint scoped by `companyId`.
 */
export default function CompanyEnergyDialog({
  open,
  companyId,
  companyName,
  onClose,
}: {
  open: boolean;
  companyId: string | null;
  companyName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<EnergyReport | null>(null);
  const [range, setRange] = useState<EnergyRange>("week");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(["analytics", "common"]);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    async function load() {
      try {
        const r = await foyerApi.fleetEnergy(range, {
          companyId: companyId!,
        });
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err, t));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, range, t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">
            {t("companyEnergyDialog.title", { name: companyName })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("companyEnergyDialog.subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={range}
            onChange={(e) => setRange(e.target.value as EnergyRange)}
            sx={{ minWidth: 120 }}
            data-testid="company-energy-range"
          >
            {RANGES.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`common:labels.${r}`)}
              </MenuItem>
            ))}
          </Select>
          <IconButton
            onClick={onClose}
            aria-label={t("common:buttons.close")}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {data ? (
          <EnergyReportPanel report={data} scope="company" />
        ) : (
          !error && (
            <Grid container spacing={2.5}>
              {[0, 1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{ p: 3 }}>
                    <Box sx={{ height: 88 }} />
                  </Card>
                </Grid>
              ))}
            </Grid>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
